import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import DatePicker from 'react-native-date-picker';
import { Ionicons } from '@expo/vector-icons';

import { Colors, ModernPalette } from '@/constants/theme';
import { getBodyFont, getHeadingFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useSupabaseAuth } from '@/lib/supabase';
import { AnimatedLoadingSwitch } from '@/components/settings/AnimatedLoadingSwitch';
import type { SummaryPreferences } from '@/types/home-summary';
import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';
import { summaryScheduler } from '@/lib/services/SummaryScheduler';
import { NotificationPreferencesManager } from '@/lib/services/NotificationPreferencesManager';

const toDateFromTime = (time: string): Date => {
  const [hours, minutes] = time.split(':').map(segment => parseInt(segment, 10));
  const date = new Date();
  date.setHours(Number.isNaN(hours) ? 0 : hours, Number.isNaN(minutes) ? 0 : minutes, 0, 0);
  return date;
};

const toTimeString = (date: Date): string => {
  return new Intl.DateTimeFormat('en-CA', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatTimeLabel = (time: string): string => {
  const [hours, minutes] = time.split(':').map(segment => parseInt(segment, 10));
  const date = new Date();
  date.setHours(Number.isNaN(hours) ? 0 : hours, Number.isNaN(minutes) ? 0 : minutes, 0, 0);
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

/**
 * AI Summaries Settings Screen
 *
 * Allows users to configure their daily AI summary preferences:
 * - Morning brief delivery time and enabled state
 * - Evening recap delivery time and enabled state
 * - Tracked member load check option
 */
export default function AISummariesScreen() {
  const colorScheme = useColorScheme();
  const router = useRouter();
  const { session } = useSupabaseAuth();
  const [summaryPreferences, setSummaryPreferences] = useState<SummaryPreferences | null>(null);
  const [isSummaryPrefsLoading, setIsSummaryPrefsLoading] = useState(false);
  const [pendingPreference, setPendingPreference] = useState<null | 'morning' | 'evening'>(null);
  const [activeTimePicker, setActiveTimePicker] = useState<null | 'morning' | 'evening'>(null);
  const [pickerDate, setPickerDate] = useState(() => {
    const now = new Date();
    now.setSeconds(0, 0);
    return now;
  });
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const palette = Colors[colorScheme ?? 'light'];
  const accentColor = palette.tint;
  const backgroundColor = palette.background;
  const textColor = palette.text;
  const textMutedColor = palette.textMuted;
  const borderMutedColor = palette.borderMuted;
  const sectionBackground = colorScheme === 'light' ? ModernPalette.white : palette.backgroundLight;
  const morningEnabled = summaryPreferences?.morningBrief.enabled ?? true;
  const morningTime = summaryPreferences?.morningBrief.time ?? '06:00';
  const eveningEnabled = summaryPreferences?.eveningRecap.enabled ?? true;
  const eveningTime = summaryPreferences?.eveningRecap.time ?? '20:00';
  const summaryLoading = isSummaryPrefsLoading && !summaryPreferences;
  const isSavingPreference = pendingPreference !== null;

  const loadSummaryPreferences = useCallback(async () => {
    if (!session) return;

    setIsSummaryPrefsLoading(true);
    try {
      const response = await authenticatedFetch.fetch('/api/home-summary/preferences');

      if (!response.ok) {
        throw new Error(`Failed to load summary preferences (${response.status})`);
      }

      const data = (await response.json()) as { preferences: SummaryPreferences };
      setSummaryPreferences(data.preferences);
      await summaryScheduler.syncPreferences(data.preferences);
    } catch (err) {
      console.error('[ai-summaries] Failed to load summary preferences:', err);
      Alert.alert(
        'AI summaries',
        'Unable to load your summary settings right now. Pull to refresh later.',
      );
    } finally {
      setIsSummaryPrefsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    loadSummaryPreferences();
  }, [loadSummaryPreferences]);

  // Load notification preferences on mount
  useEffect(() => {
    const loadNotificationPrefs = async () => {
      const manager = NotificationPreferencesManager.getInstance();
      await manager.initialize();
      const prefs = manager.getPreferences();
      setNotificationsEnabled(prefs.notificationsEnabled);
    };

    loadNotificationPrefs();
  }, []);

  // Reload notification preferences on screen focus
  useFocusEffect(
    useCallback(() => {
      const loadNotificationPrefs = async () => {
        const manager = NotificationPreferencesManager.getInstance();
        await manager.initialize();
        const prefs = manager.getPreferences();
        setNotificationsEnabled(prefs.notificationsEnabled);
      };

      loadNotificationPrefs();
    }, [])
  );

  const updateSummaryPreference = useCallback(
    async (
      update: {
        morningBrief?: Partial<{ enabled: boolean; time: string }>;
        eveningRecap?: Partial<{ enabled: boolean; time: string }>;
      },
      key: 'morning' | 'evening',
    ) => {
      if (!session) return;

      setPendingPreference(key);
      try {
        const response = await authenticatedFetch.fetch('/api/home-summary/preferences', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(update),
        });

        if (!response.ok) {
          throw new Error(`Failed to update preferences (${response.status})`);
        }

        const data = (await response.json()) as { preferences: SummaryPreferences };
        setSummaryPreferences(data.preferences);
        await summaryScheduler.syncPreferences(data.preferences);
      } catch (err) {
        console.error('[ai-summaries] Failed to update summary preferences:', err);
        Alert.alert(
          'AI summaries',
          'Something went wrong while saving your changes. Please try again.',
        );
        await loadSummaryPreferences();
      } finally {
        setPendingPreference(null);
      }
    },
    [session, loadSummaryPreferences],
  );

  const handleToggleMorning = useCallback(
    (value: boolean) => {
      updateSummaryPreference({ morningBrief: { enabled: value } }, 'morning');
    },
    [updateSummaryPreference],
  );

  const handleToggleEvening = useCallback(
    (value: boolean) => {
      updateSummaryPreference({ eveningRecap: { enabled: value } }, 'evening');
    },
    [updateSummaryPreference],
  );

  const openTimePicker = useCallback(
    (target: 'morning' | 'evening') => {
      if (isSavingPreference) return;
      if (target === 'morning' && !morningEnabled) return;
      if (target === 'evening' && !eveningEnabled) return;

      const time = target === 'morning' ? morningTime : eveningTime;
      setPickerDate(toDateFromTime(time));
      setActiveTimePicker(target);
    },
    [isSavingPreference, morningEnabled, eveningEnabled, morningTime, eveningTime],
  );

  const handleConfirmTime = useCallback(
    async (date: Date) => {
      if (!activeTimePicker) return;

      const picker = activeTimePicker;
      const nextTime = toTimeString(date);
      setActiveTimePicker(null);

      if (picker === 'morning' && nextTime !== morningTime) {
        await updateSummaryPreference({ morningBrief: { time: nextTime } }, 'morning');
      } else if (picker === 'evening' && nextTime !== eveningTime) {
        await updateSummaryPreference({ eveningRecap: { time: nextTime } }, 'evening');
      }
    },
    [activeTimePicker, morningTime, eveningTime, updateSummaryPreference],
  );

  const handleCancelPicker = useCallback(() => {
    setActiveTimePicker(null);
  }, []);

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleNavigateToNotifications = useCallback(() => {
    router.push('/notification-settings');
  }, [router]);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor }]} edges={['top']}>
      <View style={[styles.header, { borderBottomColor: borderMutedColor }]}>
        <TouchableOpacity onPress={handleBack} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={accentColor} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: textColor }]}>AI Summaries</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView style={styles.scrollView}>
        <View style={[styles.section, { backgroundColor: sectionBackground }]}>
          <View style={styles.preferenceRow}>
            <View style={styles.preferenceInfo}>
              <Text style={[styles.preferenceTitle, { color: textColor }]}>Morning Brief</Text>
              <Text style={[styles.preferenceDescription, { color: textMutedColor }]}>
                Daily three-bullet overview of today&apos;s schedule.
              </Text>
            </View>
            {pendingPreference === 'morning' ? (
              <ActivityIndicator size="small" color={accentColor} />
            ) : (
              <AnimatedLoadingSwitch
                isLoading={summaryLoading}
                targetValue={morningEnabled}
                onValueChange={handleToggleMorning}
                trackColor={{ true: accentColor, false: ModernPalette.borderLight }}
                thumbColor={ModernPalette.white}
                disabled={isSavingPreference}
              />
            )}
          </View>

          {morningEnabled ? (
            <TouchableOpacity
              style={[styles.preferenceRow, styles.preferenceSubRow]}
              onPress={() => openTimePicker('morning')}
              disabled={isSavingPreference}>
              <View style={styles.preferenceInfo}>
                <Text style={[styles.preferenceLabel, { color: textColor }]}>
                  Delivery time
                </Text>
                <Text style={[styles.preferenceDescription, { color: textMutedColor }]}>
                  We refresh one hour ahead so it&apos;s ready when you wake up.
                </Text>
              </View>
              <View style={styles.preferenceValue}>
                <Text style={[styles.preferenceValueText, { color: textColor }]}>
                  {formatTimeLabel(morningTime)}
                </Text>
                <Ionicons name="chevron-forward" size={16} color={textMutedColor} />
              </View>
            </TouchableOpacity>
          ) : null}

          <View style={[styles.preferenceDivider, { backgroundColor: borderMutedColor }]} />

          <View style={styles.preferenceRow}>
            <View style={styles.preferenceInfo}>
              <Text style={[styles.preferenceTitle, { color: textColor }]}>Evening Recap</Text>
              <Text style={[styles.preferenceDescription, { color: textMutedColor }]}>
                Wrap your day with results and a preview for tomorrow.
              </Text>
            </View>
            {pendingPreference === 'evening' ? (
              <ActivityIndicator size="small" color={accentColor} />
            ) : (
              <AnimatedLoadingSwitch
                isLoading={summaryLoading}
                targetValue={eveningEnabled}
                onValueChange={handleToggleEvening}
                trackColor={{ true: accentColor, false: ModernPalette.borderLight }}
                thumbColor={ModernPalette.white}
                disabled={isSavingPreference}
              />
            )}
          </View>

          {eveningEnabled ? (
            <>
              <TouchableOpacity
                style={[styles.preferenceRow, styles.preferenceSubRow]}
                onPress={() => openTimePicker('evening')}
                disabled={isSavingPreference}>
                <View style={styles.preferenceInfo}>
                  <Text style={[styles.preferenceLabel, { color: textColor }]}>
                    Delivery time
                  </Text>
                  <Text style={[styles.preferenceDescription, { color: textMutedColor }]}>
                    We&apos;ll send the recap at this time.
                  </Text>
                </View>
                <View style={styles.preferenceValue}>
                  <Text style={[styles.preferenceValueText, { color: textColor }]}>
                    {formatTimeLabel(eveningTime)}
                  </Text>
                  <Ionicons name="chevron-forward" size={16} color={textMutedColor} />
                </View>
              </TouchableOpacity>
            </>
          ) : null}
        </View>

        <View style={[styles.section, { backgroundColor: sectionBackground }]}>
          <TouchableOpacity
            style={styles.preferenceRow}
            onPress={handleNavigateToNotifications}>
            <View style={styles.preferenceInfo}>
              <View style={styles.notificationLinkHeader}>
                <Ionicons name="notifications-outline" size={20} color={textColor} />
                <Text style={[styles.preferenceTitle, { color: textColor }]}>
                  Notification Settings
                </Text>
              </View>
              <Text style={[styles.preferenceDescription, { color: textMutedColor }]}>
                Manage notification delivery preferences
              </Text>
            </View>
            <View style={styles.preferenceValue}>
              <Text style={[styles.preferenceValueText, { color: textMutedColor }]}>
                {notificationsEnabled ? 'On' : 'Off'}
              </Text>
              <Ionicons name="chevron-forward" size={16} color={textMutedColor} />
            </View>
          </TouchableOpacity>
        </View>

        <View style={styles.footer} />
      </ScrollView>

      <DatePicker
        modal
        open={activeTimePicker !== null}
        date={pickerDate}
        mode="time"
        onConfirm={handleConfirmTime}
        onCancel={handleCancelPicker}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
    fontFamily: getHeadingFont(),
  },
  headerSpacer: {
    width: 32,
  },
  scrollView: {
    flex: 1,
  },
  section: {
    marginTop: 20,
    borderRadius: 12,
    marginHorizontal: 16,
    overflow: 'hidden',
  },
  preferenceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  preferenceSubRow: {
    paddingLeft: 32,
  },
  preferenceInfo: {
    flex: 1,
    marginRight: 12,
  },
  notificationLinkHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  preferenceTitle: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: getBodyFont(),
  },
  preferenceLabel: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: getBodyFont(),
  },
  preferenceDescription: {
    fontSize: 12,
    marginTop: 4,
    fontFamily: getBodyFont(),
  },
  preferenceValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  preferenceValueText: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: getBodyFont(),
  },
  preferenceDivider: {
    height: StyleSheet.hairlineWidth,
    marginLeft: 16,
    marginRight: 16,
  },
  footer: {
    height: 40,
  },
});
